import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, ListItemButton, Tooltip, Typography, useTheme } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { DndContext, DragOverlay, useDraggable, useDroppable } from "@dnd-kit/core";
import { addMonths, isSameDay, isToday, isWeekend } from "date-fns";
import { FormattedMessage, useIntl } from "react-intl";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "../../cmms/components/CmmsStates";
import {
  cmmsTypeScale,
  getAssetHierarchyPalette,
  getAssetHierarchyPanelSx,
  getIconButtonSx,
  getOutlinedButtonSx,
  getPanelTitleSx,
} from "../../cmms/assetHierarchyStyles";
import { getTypeBadge } from "../workOrderEnums";
import { getEstimateTotal, getMoveTargetsIn } from "../workOrderWeek";
import { getMonthCells, getMonthGrid, getMonthRows, getMonthStart, getProjectedMonthRows } from "../workOrderMonth";
import { useProjectedOccurrences } from "../useProjectedOccurrences";
import { useWorkOrderMove } from "../useWorkOrderMove";
import {
  DayHours,
  EstimateSummary,
  LONG_DAY_OPTIONS,
  MOVE_HOST_SX,
  MoveMenu,
  MoveUndoSnackbar,
  SCREEN_READER_ONLY_SX,
  TYPE_ICONS,
  TradeTag,
  WeekCardContent,
  getCalendarShades,
  getCardSx,
  getCardWhen,
  getDropTargetSx,
  isDropTarget,
  useDragToMove,
} from "./workOrderCalendarParts";
import messages from "../messages";

// The week number's column, wide enough for a week's hours ("62h 30m") under the number, then the
// days. A day is narrower than in the week, since a card here is one line.
const WEEK_COLUMN_WIDTH = 64;
const DAY_MIN_WIDTH = 112;
const GRID_COLUMNS = `${WEEK_COLUMN_WIDTH}px repeat(7, minmax(${DAY_MIN_WIDTH}px, 1fr))`;
const GRID_MIN_WIDTH = WEEK_COLUMN_WIDTH + 7 * DAY_MIN_WIDTH;

// A day shows this many lines: as many cards, or one fewer and how many more there are.
const DAY_LINES = 4;
// The day's number and four lines, so every closed week is as tall as the busiest can be.
const DAY_MIN_HEIGHT = 140;

const keepAll = (occurrences) => occurrences;

// Today before the weekend, since a Saturday shift is still today, as in the week.
const getDayBg = (date, shades) => {
  if (isToday(date)) return shades.today;
  if (isWeekend(date)) return shades.weekend;
  return undefined;
};

/*
 * What a month card leaves out, on hover or focus: the week's card in full, with the department
 * and the trade the week shows as its panels.
 *
 * It waits a moment, also when moving from one card to the next, so a pointer crossing the month
 * does not flash a tooltip at every card it passes. It lets the pointer through to the cards under
 * it, and it describes the card rather than naming it, so a screen reader still reads the card's
 * own line first.
 *
 * None while a card is dragged (dragging), when it would cover the days being dropped on. That
 * includes one asked for before the drag began: a touch held still to pick a card up is also how
 * a touch screen asks for a tooltip, and it asks after the drag has started.
 */
function MonthCardTooltip({ row, acute, dragging, assetNodeNameById, userNameById, tradeNameById, palette, children }) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const draggingRef = useRef(dragging);
  useEffect(() => {
    draggingRef.current = dragging;
    if (dragging) setOpen(false);
  }, [dragging]);

  const { workOrder } = row;
  const departmentId = workOrder.assignedDepartmentId;
  const department = departmentId != null ? (assetNodeNameById?.get(departmentId) ?? `#${departmentId}`) : null;
  // A projection names its own trade, for a trade the filter does not offer any more.
  const trade = workOrder.tradeId != null
    ? (tradeNameById.get(workOrder.tradeId) ?? workOrder.tradeName ?? `#${workOrder.tradeId}`)
    : null;
  const groups = [
    department ? intl.formatMessage(messages.monthTooltipDepartment, { name: department }) : null,
    trade ? intl.formatMessage(messages.monthTooltipTrade, { name: trade }) : null,
  ].filter(Boolean);

  return (
    <Tooltip
      describeChild
      disableInteractive
      enterDelay={400}
      enterNextDelay={400}
      open={open && !dragging}
      onOpen={() => { if (!draggingRef.current) setOpen(true); }}
      onClose={() => setOpen(false)}
      placement="right-start"
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: palette.surface,
            border: `1px solid ${palette.border}`,
            borderRadius: 0,
            boxShadow: palette.shadow,
            color: palette.text,
            maxWidth: 300,
            p: 1.25,
          },
        },
      }}
      title={
        <>
          <WeekCardContent
            row={row}
            acute={acute}
            hasMoveButton={false}
            assetNodeNameById={assetNodeNameById}
            userNameById={userNameById}
            palette={palette}
          />
          {groups.length > 0 &&
            <Typography variant="caption" sx={{ color: palette.mutedText, display: "block", mt: 0.25 }}>
              {groups.join(" · ")}
            </Typography>
          }
        </>
      }
    >
      {children}
    </Tooltip>
  );
}

// The week's card, on one line.
const getMonthCardSx = (projected, typeColor, palette) => ({
  ...getCardSx(projected, typeColor, palette),
  px: 0.75,
  py: "2px",
});

/*
 * What a month card says, on one line: the akut icon, the trade's tag where the cards are told to
 * show it (tradeTagById), the time where the time means something (getCardWhen), and the title,
 * cut short where the day is too narrow for it. A screen reader hears the trade after the title,
 * in full.
 */
function MonthCardLine({ row, acute, tradeTagById, palette }) {
  const intl = useIntl();
  const { workOrder, projected } = row;
  const type = getTypeBadge(workOrder.workOrderType, intl);
  const TypeIcon = TYPE_ICONS[workOrder.workOrderType];
  const when = getCardWhen(intl, row, acute);
  const tradeTag = workOrder.tradeId != null ? tradeTagById?.get(workOrder.tradeId) : undefined;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {TypeIcon &&
        <TypeIcon sx={{ color: type.bg, flexShrink: 0, fontSize: 14 }} />
      }
      {tradeTag &&
        <TradeTag tag={tradeTag} />
      }
      <Typography variant="caption" noWrap sx={{ color: palette.text, lineHeight: "18px", minWidth: 0 }}>
        {when &&
          <Box component="span" sx={{ color: palette.mutedText, mr: 0.5 }}>{when}</Box>
        }
        <Box component="span" sx={{ fontWeight: 700 }}>{workOrder.title}</Box>
        {tradeTag &&
          <Box component="span" sx={SCREEN_READER_ONLY_SX}>
            {` · ${intl.formatMessage(messages.monthTooltipTrade, { name: tradeTag.name })}`}
          </Box>
        }
        {/* What the card's dashed border says to the eye. */}
        {projected &&
          <Box component="span" sx={SCREEN_READER_ONLY_SX}>{` · ${intl.formatMessage(messages.weekProjected)}`}</Box>
        }
      </Typography>
    </Box>
  );
}

/*
 * One order in a day, on one line with the type's edge (MonthCardLine). The rest of what the
 * week's card says is in its tooltip.
 *
 * A projected card opens nothing and is a plain dashed box, as in the week.
 *
 * A manager moves preventive work or a problem to another day on screen, from today on (the days
 * getMoveTargetsIn gives from the grid's days), by dragging it there or from its menu, as in the
 * week. onMove is only passed to a manager. A card with nowhere to go has neither, so a month
 * that has gone has none. The menu's button covers the end of the title while the card is pointed
 * at or has focus; on a touch screen it is always there, so the title stops short of it.
 */
function MonthCard({ row, acute, onClick, days, onMove, movingId, dragging, tradeTagById, palette, ...tooltipProps }) {
  const intl = useIntl();
  const { workOrder, projected } = row;
  const type = getTypeBadge(workOrder.workOrderType, intl);
  const moveTargets = onMove ? getMoveTargetsIn(row, days) : [];
  const canMove = moveTargets.length > 0;
  // Not while a move is still on its way, since handleMove would drop the second one unsaid.
  const { setNodeRef, listeners, isDragging } = useDraggable({
    id: row.key,
    data: { row, acute },
    disabled: !canMove || movingId !== null,
  });

  const cardSx = getMonthCardSx(projected, type.bg, palette);
  const line = <MonthCardLine row={row} acute={acute} tradeTagById={tradeTagById} palette={palette} />;

  // Faded while it is dragged or its move is on its way, as in the week.
  const card = projected
    ? <Box data-month-card sx={cardSx}>{line}</Box>
    : (
      <ListItemButton
        ref={setNodeRef}
        {...listeners}
        data-month-card
        data-work-order-id={workOrder.id}
        onClick={() => onClick?.(workOrder)}
        sx={{
          ...cardSx,
          opacity: isDragging || movingId === workOrder.id ? 0.5 : 1,
          ...(canMove
            ? { touchAction: "manipulation", WebkitTouchCallout: "none", "@media (hover: none)": { pr: "24px" } }
            : {}),
          "&:hover": { backgroundColor: palette.surface, borderColor: palette.selectedBorder, borderLeftColor: type.bg },
        }}
      >
        {line}
      </ListItemButton>
    );

  const withTooltip = (
    <MonthCardTooltip row={row} acute={acute} dragging={dragging} palette={palette} {...tooltipProps}>
      {card}
    </MonthCardTooltip>
  );

  if (!canMove) return withTooltip;

  return (
    <Box sx={MOVE_HOST_SX}>
      {withTooltip}
      <MoveMenu dense row={row} targets={moveTargets} onMove={onMove} palette={palette} />
    </Box>
  );
}

// The card under the pointer while it is dragged, as in the week: a copy, with the shadow menus have.
function DraggedMonthCard({ row, acute, tradeTagById, palette }) {
  const intl = useIntl();
  const type = getTypeBadge(row.workOrder.workOrderType, intl);
  return (
    <Box sx={{ ...getMonthCardSx(false, type.bg, palette), boxShadow: palette.shadow, cursor: "grabbing" }}>
      <MonthCardLine row={row} acute={acute} tradeTagById={tradeTagById} palette={palette} />
    </Box>
  );
}

const toCards = (day) => [
  ...day.acute.map((row) => ({ row, acute: true })),
  ...day.planned.map((row) => ({ row, acute: false })),
];

/*
 * One day of the month: its number, its hours, and its cards, akut first as in the week.
 *
 * A day has room for DAY_LINES lines. A busier one shows one card fewer and how many more there
 * are, which opens its whole week, so the week's days stay side by side and every card in them can
 * be reached. "Show fewer" closes it again. Focus stays with the reader: on the first card the
 * button opened, or back on the button once the week is closed.
 *
 * A day from the month before or after is shown, with its orders, and only its number is dimmed.
 *
 * While a card is dragged, a day it can be dropped on is outlined as in the week, a closed one as
 * a whole. Any other day takes no drop and stays as it is.
 */
function MonthDayCell({ day, expanded, onToggleWeek, dropTarget, showEstimates, shades, palette, cardProps }) {
  const intl = useIntl();
  const { date, inMonth, count, estimatedHours, withoutEstimateCount } = day;
  const today = isToday(date);
  const cards = toCards(day);
  const overflows = cards.length > DAY_LINES;
  const shown = overflows && !expanded ? cards.slice(0, DAY_LINES - 1) : cards;

  const { setNodeRef, isOver } = useDroppable({
    id: `month:${date.getTime()}`,
    data: { date },
    disabled: !dropTarget,
  });
  const cellRef = useRef(null);
  const setCellRef = useCallback((node) => {
    cellRef.current = node;
    setNodeRef(node);
  }, [setNodeRef]);
  const toggledHere = useRef(false);
  useEffect(() => {
    if (!toggledHere.current) return;
    toggledHere.current = false;
    const cell = cellRef.current;
    const toggle = cell?.querySelector("[data-month-toggle]");
    const revealed = expanded
      ? [...(cell?.querySelectorAll("[data-month-card]") ?? [])].slice(DAY_LINES - 1).find((card) => card.tabIndex >= 0)
      : null;
    (revealed ?? toggle)?.focus();
  }, [expanded]);

  const handleToggle = () => {
    toggledHere.current = true;
    onToggleWeek();
  };

  return (
    <Box
      ref={setCellRef}
      role={count > 0 ? "group" : undefined}
      aria-label={count > 0 ? intl.formatDate(date, LONG_DAY_OPTIONS) : undefined}
      aria-current={today ? "date" : undefined}
      sx={{
        backgroundColor: getDayBg(date, shades),
        borderLeft: `1px solid ${palette.border}`,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
        minHeight: DAY_MIN_HEIGHT,
        minWidth: 0,
        p: 0.5,
        ...(dropTarget ? getDropTargetSx(isOver, palette) : {}),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 0.5, px: 0.25 }}>
        {/* The 1st says its month, so where one month ends and the next begins is read, not counted. */}
        <Typography
          variant="body2"
          sx={{
            color: today ? palette.accent : inMonth ? palette.text : palette.mutedText,
            fontWeight: today ? 700 : 500,
          }}
        >
          {intl.formatDate(date, date.getDate() === 1 ? { day: "numeric", month: "short" } : { day: "numeric" })}
        </Typography>
        {showEstimates && count > 0 &&
          <Typography variant="caption" sx={{ color: palette.text, fontWeight: 700 }}>
            <DayHours estimatedHours={estimatedHours} withoutEstimateCount={withoutEstimateCount} />
          </Typography>
        }
      </Box>
      {shown.map(({ row, acute }) => (
        <MonthCard key={row.key} row={row} acute={acute} palette={palette} {...cardProps} />
      ))}
      {overflows &&
        <Button
          size="small"
          data-month-toggle
          onClick={handleToggle}
          sx={{
            alignSelf: "flex-start",
            color: palette.mutedText,
            fontWeight: 700,
            lineHeight: "20px",
            minWidth: 0,
            px: 0.75,
            py: 0,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.buttonHover, color: palette.text },
          }}
        >
          {expanded
            ? intl.formatMessage(messages.monthShowLess)
            : intl.formatMessage(messages.monthMore, { count: cards.length - (DAY_LINES - 1) })}
        </Button>
      }
    </Box>
  );
}

/*
 * One week of the month: its number, with its hours when the month shows hours, then its seven
 * days. A screen reader is told the week by name, so the short "v. 43" is left to the eye.
 */
function MonthWeekRow({ week, expanded, onToggle, drag, showEstimates, shades, palette, cardProps }) {
  const intl = useIntl();
  return (
    <Box
      role="group"
      aria-label={intl.formatMessage(messages.monthWeekAria, { weekNumber: week.weekNumber })}
      sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, borderTop: `1px solid ${palette.border}` }}
    >
      <Box sx={{ minWidth: 0, px: 1, py: 0.75 }}>
        <Typography variant="caption" aria-hidden sx={{ color: palette.mutedText, display: "block", fontWeight: 700 }}>
          {intl.formatMessage(messages.monthWeekNumber, { weekNumber: week.weekNumber })}
        </Typography>
        {showEstimates && week.count > 0 &&
          <Typography variant="caption" sx={{ color: palette.text, display: "block", fontWeight: 700 }}>
            <DayHours estimatedHours={week.estimatedHours} withoutEstimateCount={week.withoutEstimateCount} />
          </Typography>
        }
      </Box>
      {week.days.map((day) => (
        <MonthDayCell
          key={day.date.getTime()}
          day={day}
          expanded={expanded}
          onToggleWeek={onToggle}
          dropTarget={isDropTarget(drag, day.date)}
          showEstimates={showEstimates}
          shades={shades}
          palette={palette}
          cardProps={cardProps}
        />
      ))}
    </Box>
  );
}

/*
 * The work still to do from before the first day on screen, in a strip of its own above the
 * month, as the week has a column of its own before Monday. Each card says its own date. Its cards
 * move to a day like any other, and nothing is dropped on it.
 */
function LateStrip({ late, showEstimates, shades, palette, cardProps }) {
  const intl = useIntl();
  return (
    <Box
      role="group"
      aria-label={intl.formatMessage(messages.weekOverdue)}
      sx={{ ...getAssetHierarchyPanelSx(palette), mb: 2 }}
    >
      <Box sx={{ backgroundColor: shades.late, p: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 1, mb: 0.75, px: 0.25 }}>
          <Tooltip title={intl.formatMessage(messages.monthOverdueTooltip)}>
            <Typography variant="body2" sx={{ color: shades.lateText, cursor: "help", fontWeight: 700 }}>
              {intl.formatMessage(messages.weekOverdue)}
            </Typography>
          </Tooltip>
          <Typography variant="caption" sx={{ color: palette.mutedText }}>
            {intl.formatMessage(messages.weekDayCount, { count: late.count })}
            {showEstimates &&
              <>
                {" · "}
                <Box component="span" sx={{ color: palette.text, fontWeight: 700 }}>
                  <DayHours estimatedHours={late.estimatedHours} withoutEstimateCount={late.withoutEstimateCount} />
                </Box>
              </>
            }
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gap: 0.5, gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
          {toCards(late).map(({ row, acute }) => (
            <MonthCard key={row.key} row={row} acute={acute} palette={palette} {...cardProps} />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

/*
 * The work-orders page as a month calendar: every order the page's filters let through, akut and
 * planned, on the day it falls on, a line each. The week overview's rules over the month's weeks
 * (workOrderMonth.js), with one calendar for all of it rather than a panel per department or
 * trade. The tooltip names those instead, and a card tags its trade where tradeTagById is passed.
 * The page passes it only while the trade filter is on every trade: with one trade picked, every
 * card would say the same.
 *
 * Reads the same filtered list as the list and the week, and from the current month on shows what
 * the schedules are still to raise, filtered the same way (filterProjected). In the current month,
 * work still open from before the first day on screen is in a strip above it.
 *
 * A manager moves a card to another day on screen as in the week, by dragging it or from its menu
 * (useWorkOrderMove), with the same Undo.
 *
 * Always opens on the current month, as the week opens on the current week.
 */
export default function WorkOrderMonthView({
  companyKey,
  workOrders,
  isLoading,
  assetNodeNameById,
  userNameById,
  tradeOptions,
  tradeTagById,
  filterProjected = keepAll,
  onRowClick,
}) {
  const intl = useIntl();
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);

  const [monthStart, setMonthStart] = useState(() => getMonthStart());
  // The weeks opened past their four lines, by their Monday. Not kept from one month to the next:
  // an opened week is a look at one busy week, not a setting.
  const [expandedWeeks, setExpandedWeeks] = useState(() => new Set());

  const currentMonthStart = getMonthStart();
  const isCurrentMonth = monthStart.getTime() === currentMonthStart.getTime();
  const grid = useMemo(() => getMonthGrid(monthStart), [monthStart]);

  const showMonth = (month) => {
    setMonthStart(month);
    setExpandedWeeks(new Set());
  };

  const toggleWeek = (weekStart) => setExpandedWeeks((current) => {
    const next = new Set(current);
    const key = weekStart.getTime();
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });

  // What the schedules are still to raise in the weeks on screen. Not for weeks that have all
  // gone, the same rule as the week's.
  const showsProjection = Boolean(companyKey) && grid.end > new Date();
  const projectedQuery = useProjectedOccurrences(companyKey, grid.start, grid.end, showsProjection);
  const occurrences = showsProjection ? projectedQuery.data : null;

  // Both lists before anything is drawn, as in the week.
  const isMonthLoading = isLoading || (showsProjection && projectedQuery.isLoading);

  // The filter's options, so a retired trade is named here the way the dropdown names it.
  const tradeNameById = useMemo(
    () => new Map((tradeOptions ?? []).map((option) => [option.id, option.title])),
    [tradeOptions],
  );

  const { cells, isEmpty, showEstimates } = useMemo(() => {
    const rows = [
      ...getMonthRows(workOrders, grid),
      ...getProjectedMonthRows(filterProjected(occurrences ?? []), grid),
    ];
    return {
      cells: getMonthCells(rows, grid),
      isEmpty: rows.length === 0,
      // The week's rule: hours once anything on screen has an estimate.
      showEstimates: getEstimateTotal(rows).estimatedHours !== null,
    };
  }, [workOrders, occurrences, filterProjected, grid]);

  // Moving a card to another day, from its menu or by dragging it there.
  const monthRef = useRef(null);
  const { canMove, movingId, handleMove, notice, closeNotice, handleUndo } = useWorkOrderMove({
    companyKey,
    containerRef: monthRef,
    periodKey: monthStart.getTime(),
    layout: cells,
  });

  /*
   * A day with no line to spare opens its week as a card is moved there, or moved back there by
   * Undo. Behind its "+N more" the card would not be seen where it went, and focus could not
   * follow it.
   */
  const openWeekOf = (date) => {
    if (movingId !== null) return;
    const week = cells.weeks.find((candidate) => candidate.days.some((cell) => isSameDay(cell.date, date)));
    const day = week?.days.find((cell) => isSameDay(cell.date, date));
    if (day && day.count >= DAY_LINES) {
      setExpandedWeeks((current) => new Set(current).add(week.start.getTime()));
    }
  };

  const moveToDay = (row, day) => {
    openWeekOf(day);
    handleMove(row, day);
  };

  const undoMove = () => {
    if (notice.move?.undoStart) openWeekOf(notice.move.undoStart);
    handleUndo();
  };

  const { drag, dndProps } = useDragToMove({
    getTargets: (row) => getMoveTargetsIn(row, grid.days),
    onMove: moveToDay,
  });

  const { monthTotals } = cells;
  const monthLabel = intl.formatDate(monthStart, { month: "long", year: "numeric" });
  const cardProps = {
    assetNodeNameById,
    userNameById,
    tradeNameById,
    tradeTagById,
    onClick: onRowClick,
    days: grid.days,
    onMove: canMove ? moveToDay : null,
    movingId,
    dragging: drag !== null,
  };
  const shades = getCalendarShades(theme, cmmsColors);

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mb: 2 }}>
        <Tooltip title={intl.formatMessage(messages.monthPrevious)}>
          <IconButton
            aria-label={intl.formatMessage(messages.monthPrevious)}
            onClick={() => showMonth(addMonths(monthStart, -1))}
            sx={getIconButtonSx(cmmsColors)}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Tooltip>
        {/* Fixed width, so stepping from "maj" to "september" does not move the arrows. */}
        <Typography
          variant={cmmsTypeScale.statValue}
          sx={{
            ...getPanelTitleSx(cmmsColors),
            minWidth: { sm: 200 },
            textAlign: "center",
            // Swedish writes a month in lower case, which reads as a heading only with a capital.
            "&::first-letter": { textTransform: "uppercase" },
          }}
        >
          {monthLabel}
        </Typography>
        <Tooltip title={intl.formatMessage(messages.monthNext)}>
          <IconButton
            aria-label={intl.formatMessage(messages.monthNext)}
            onClick={() => showMonth(addMonths(monthStart, 1))}
            sx={getIconButtonSx(cmmsColors)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Tooltip>
        <Button
          variant="outlined"
          onClick={() => showMonth(currentMonthStart)}
          disabled={isCurrentMonth}
          sx={getOutlinedButtonSx(cmmsColors)}
        >
          <FormattedMessage {...messages.monthCurrent} />
        </Button>
        {/* The month's own days and what is late, which is what its name promises. */}
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, ml: 1 }}>
          {intl.formatMessage(messages.weekCounts, { acute: monthTotals.acute, planned: monthTotals.planned })}
        </Typography>
        {showEstimates &&
          <EstimateSummary
            estimatedHours={monthTotals.estimatedHours}
            withoutEstimateCount={monthTotals.withoutEstimateCount}
            palette={cmmsColors}
          />
        }
      </Box>

      {isMonthLoading &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), p: 2 }}>
          <CmmsListSkeleton rows={6} height={36} />
        </Box>
      }

      {/* Above the month, not in place of it: the raised orders are still right. */}
      {!isMonthLoading && showsProjection && projectedQuery.isError &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), mb: 2 }}>
          <CmmsErrorState
            dense
            error={projectedQuery.error}
            fallback={intl.formatMessage(messages.monthProjectedLoadFailed)}
            onRetry={() => projectedQuery.refetch()}
            isRetrying={projectedQuery.isFetching}
          />
        </Box>
      }

      {!isMonthLoading && isEmpty &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), py: 4 }}>
          <CmmsEmptyState
            icon={<EventBusyIcon />}
            title={intl.formatMessage(messages.monthEmptyTitle, { month: monthLabel })}
            hint={intl.formatMessage(messages.monthEmptyHint)}
          />
        </Box>
      }

      {!isMonthLoading && !isEmpty &&
        <DndContext {...dndProps}>
          <Box ref={monthRef}>
            {cells.late.count > 0 &&
              <LateStrip
                late={cells.late}
                showEstimates={showEstimates}
                shades={shades}
                palette={cmmsColors}
                cardProps={cardProps}
              />
            }

            {/*
              * A day is never narrower than a line can be read at. Where the screen has no room for
              * seven of them, the month scrolls sideways inside the panel, as the week does.
              */}
            <Box sx={getAssetHierarchyPanelSx(cmmsColors)}>
              <Box sx={{ overflowX: "auto" }}>
                <Box sx={{ minWidth: GRID_MIN_WIDTH }}>
                  {/* Each day says its own name to a screen reader, so the header is for the eye. */}
                  <Box aria-hidden sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS }}>
                    <Box />
                    {grid.weeks[0].days.map((date) => (
                      <Typography
                        key={date.getDay()}
                        variant="caption"
                        sx={{
                          borderLeft: `1px solid ${cmmsColors.border}`,
                          color: cmmsColors.mutedText,
                          fontWeight: 700,
                          px: 1,
                          py: 0.75,
                        }}
                      >
                        {intl.formatDate(date, { weekday: "short" })}
                      </Typography>
                    ))}
                  </Box>
                  {cells.weeks.map((week) => (
                    <MonthWeekRow
                      key={week.start.getTime()}
                      week={week}
                      expanded={expandedWeeks.has(week.start.getTime())}
                      onToggle={() => toggleWeek(week.start)}
                      drag={drag}
                      showEstimates={showEstimates}
                      shades={shades}
                      palette={cmmsColors}
                      cardProps={cardProps}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
          {/* No animation on a drop: the card is drawn on its new day as soon as the move is saved. */}
          <DragOverlay dropAnimation={null}>
            {drag &&
              <DraggedMonthCard row={drag.row} acute={drag.acute} tradeTagById={tradeTagById} palette={cmmsColors} />
            }
          </DragOverlay>
        </DndContext>
      }

      <MoveUndoSnackbar notice={notice} onClose={closeNotice} onUndo={undoMove} />
    </Box>
  );
}
